import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ChatDropdownPortal.scss';

function ChatDropdownPortal({ children, isOpen, onClose, anchorRef, className, offset = 8 }) {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);

  // Use useLayoutEffect to prevent flicker on initial open
  useLayoutEffect(() => {
    if (isOpen && anchorRef?.current) {
      const updatePosition = () => {
        const rect = anchorRef.current.getBoundingClientRect();
        const headerRect = anchorRef.current.closest('.chatbot__header-wrapper')?.getBoundingClientRect();

        // Use header width if available, otherwise use window width
        const containerWidth = headerRect?.width || window.innerWidth;
        const padding = 16; // Match the dropdown's padding

        setPosition({
          top: rect.bottom + offset,
          left: (headerRect?.left ?? 0) + padding, // Fixed: Use nullish coalescing
          width: containerWidth - padding * 2,
        });
      };

      // Initial position update
      updatePosition();

      // Setup ResizeObserver with safety check for older browsers/tests
      let resizeObserver;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(updatePosition); // Smooth updates
        });
        resizeObserver.observe(anchorRef.current);

        // Also observe header container for width changes
        const headerElement = anchorRef.current.closest('.chatbot__header-wrapper');
        if (headerElement) {
          resizeObserver.observe(headerElement);
        }
      }

      // Use capture phase to catch all scroll events (including nested containers)
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        resizeObserver?.disconnect();
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, anchorRef, offset]);

  // Handle escape key
  useEffect(() => {
    if (isOpen) {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Invisible overlay to catch all clicks outside */}
      <div
        className="chat-dropdown-overlay"
        onClick={onClose}
        onMouseDown={onClose} // Also handle mousedown for reliability
        aria-hidden="true"
      />

      {/* Actual dropdown positioned absolutely */}
      <div
        ref={dropdownRef}
        className={`chat-dropdown-portal ${className || ''}`}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${position.width}px`,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()} // Prevent premature closes
      >
        {children}
      </div>
    </>,
    document.body
  );
}

ChatDropdownPortal.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }), // Improved PropType
  className: PropTypes.string,
  offset: PropTypes.number,
};

ChatDropdownPortal.defaultProps = {
  anchorRef: null,
  className: '',
  offset: 8,
};

export default ChatDropdownPortal;
